import React, {useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import clsx from 'classnames';
import internationalization from './intl';
import {startDelayedIsLoading} from "../../hooks/common/loading";
import {
  loginUser as login,
  setStayLoggedInPreference,
  stayLoggedInPreference
} from "../../js/services/LoginService";
import {useDispatch} from "react-redux";
import {
  isLoading as isLoadingAction,
} from '../../js/actions/index';
import {useNavigate} from "react-router-dom";
import { startAuthentication as startPasskeyAuth, finishAuthentication as finishPasskeyAuth } from '../../js/services/PasskeyService';
import { updateCredentialsStorage } from '../../js/services/LoginService';
import { pendingDirectErpCallbackUrl } from '../../js/DirectErpService';
import MarketingAuthLayout from "../shared/MarketingAuthLayout";
import TrademarkNotice from "../shared/TrademarkNotice";
import {trackEvent} from "../../js/services/AnalyticsService";

const formWrapperClasses = 'flex w-full flex-col gap-6 sm:gap-7';
const headerSalutationClasses = 'text-sm font-medium uppercase tracking-[0.2em] text-indigo-500';
const headerTitleClasses = 'text-3xl font-semibold text-slate-900 sm:text-4xl';
const headerSubtitleClasses = 'text-base leading-relaxed text-slate-500 sm:text-lg sm:leading-7';
const fieldLabelClasses = 'text-sm font-medium text-slate-600';
const inputClasses = 'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 transition placeholder:text-slate-400 focus:border-indigo-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-200';
const checkboxClasses = 'h-4 w-4 rounded border-slate-300 text-indigo-600 accent-indigo-600 focus:ring-indigo-200 focus:outline-hidden';
const primaryButtonClasses = 'inline-flex w-full items-center justify-center rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-200 sm:w-auto';
const outlineButtonClasses = 'inline-flex w-full items-center justify-center rounded-2xl border border-indigo-200 bg-white px-5 py-3 text-sm font-semibold text-indigo-600 transition hover:border-indigo-300 hover:bg-indigo-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-100 sm:w-auto';
const secondaryButtonClasses = 'inline-flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-100 sm:w-auto';
const errorClasses = 'rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600';
const twoFactorWrapperClasses = 'flex flex-col gap-4 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-5 text-indigo-900 shadow-sm';
const helperLinkClasses = 'rounded-full px-3 py-1 text-sm font-semibold text-indigo-600 transition hover:text-indigo-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-200';
const dividerClasses = 'flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-slate-400';
const dividerBarClasses = 'h-px flex-1 bg-slate-200/60';


// ?path= (set by PrivateRoute) is only followed back to the KI-Integration consent page, so it
// cannot be abused as an open redirect.
export const consentReturnPath = (search = window.location.search) => {
  const path = new URLSearchParams(search).get('path');
  return path && /^\/oauth\/consent\?request=[A-Za-z0-9_-]+$/.test(path) ? path : null;
};

const Login = () => {

  const dispatch = useDispatch();
  const setIsLoading = (loading) => dispatch(isLoadingAction(loading));

  // Prefetch Register chunk so navigation is instant when users click "Register" [SEPAHELD-FRONTEND-PERF]
  useEffect(() => {
    import(/* webpackPrefetch: true */ "../Register");
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState(undefined);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [error, setError] = useState();
  const [stayLoggedIn, setStayLoggedIn] = useState(stayLoggedInPreference());

  const navigate = useNavigate();

  // After a successful login, resume a pending WISO MeinBüro / invoiz connection
  // (started from the provider before the customer was authenticated) by routing
  // back to its callback page, or return to the KI-Integration consent page an AI
  // assistant sent the user to; otherwise land on the app as usual.
  const postLoginTarget = () => pendingDirectErpCallbackUrl() || consentReturnPath() || '/app';

  const redirectRegister = () => {
    navigate('/register');
  }

  const clearPassword = () => {
    setPassword("");
  }

  const passwordForgotten = () => {
    navigate('/forgotten-password');
  }

  const onSubmitPasswordForm = () => {
    const stopDelayedIsLoading = startDelayedIsLoading(setIsLoading);

    const loginObj = {
      email,
      password,
      twoFactorCode,
    };

    setError(undefined);
    login(loginObj)
        .then((res) => {
          if (res && res.twoFactorRequired) {
            setTwoFactorRequired(true);
            stopDelayedIsLoading();
            setError({ errorMessageId: 'login.twofactor.required' });
            return;
          }
          stopDelayedIsLoading();
          clearPassword();
          setTwoFactorCode(undefined);
          setTwoFactorRequired(false);
          trackEvent('login', {method: twoFactorCode ? 'password_2fa' : 'password'});
          navigate(postLoginTarget());

        })
        .catch((err) => {
          stopDelayedIsLoading();

          if (err.response && err.response.status === 428) {
            setTwoFactorRequired(true);
            setError({ errorMessageId: 'login.twofactor.required' });
            return;
          }
          clearPassword();
          trackEvent('login_error', {method: 'password'});
          setError({
            errorMessageId: "unified.register.password.error",
            showMissingInfoErrorMessage: true,
          });
        })
  }

  // Dynamically import @simplewebauthn/browser (~124KB) only when needed [SEPAHELD-FRONTEND-PERF]
  const onPasskeyLogin = async () => {
    if (!email) {
      setError({ errorMessageId: 'password.forgot.email.required' });
      return;
    }

    const stopDelayedIsLoadingFn = startDelayedIsLoading(setIsLoading);

    try {
      const { browserSupportsWebAuthn, startAuthentication } = await import('@simplewebauthn/browser');

      if (!browserSupportsWebAuthn()) {
        setError({ errorMessageId: 'passkey.unsupported' });
        stopDelayedIsLoadingFn();
        return;
      }

      const options = await startPasskeyAuth(email);

      if (!options) {
        setError({ errorMessageId: 'passkey.login.error' });
        return;
      }

      const { hints, extensions: _extensions, timeout: _timeout, userVerification: _userVerification, ...requestOptions } = options;
      const hintedUserId = Array.isArray(hints) && hints.length ? hints[0] : undefined;
      const userId = hintedUserId || options?.userId || options?.userHandle;

      if (!userId) {
        setError({ errorMessageId: 'passkey.no.user' });
        return;
      }

      const authData = await startAuthentication(requestOptions);
      const result = await finishPasskeyAuth(userId, JSON.stringify(authData));

      if (!result?.jwtToken) {
        throw new Error('Missing credentials after passkey login');
      }

      updateCredentialsStorage({
        jwtToken: result?.jwtToken,
        userId: result?.user?.id,
        customerId: result?.user?.customers?.[0]?.id,
      });

      trackEvent('login', {method: 'passkey'});
      navigate(postLoginTarget());
    } catch (e) {
      console.error('Passkey login failed', e);
      setError({ errorMessageId: 'passkey.login.error' });
    } finally {
      stopDelayedIsLoadingFn();
    }
  }

  const handleFormSubmit = (event) => {
    event?.preventDefault();
    onSubmitPasswordForm();
  };

  return (
      <MarketingAuthLayout panelClassName="xl:max-w-136">
          <form onSubmit={handleFormSubmit} className={formWrapperClasses}>
              <div className="flex flex-col gap-3 text-left">
                  <span className={headerSalutationClasses}>
                      <FormattedMessage {...internationalization.formSalutation}/>
                  </span>
                  <h2 className={headerTitleClasses}>
                      <FormattedMessage {...internationalization.formTitle}/>
                  </h2>
                  <p className={headerSubtitleClasses}>
                      <FormattedMessage {...internationalization.formSubtitle}/>
                  </p>
              </div>

              <div className="flex flex-col gap-5">
                  <label className={fieldLabelClasses} htmlFor="email">
                      <FormattedMessage {...internationalization.email} />
                  </label>
                  <div className="relative flex items-center">
                      <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(event) => {
                              setEmail(event.target.value);
                              setError(undefined);
                          }}
                          className={inputClasses}
                          autoComplete="email"
                      />
                  </div>

                  <label className={fieldLabelClasses} htmlFor="password">
                      <FormattedMessage {...internationalization.passwortLabel}/>
                  </label>
                  <div className="relative flex items-center">
                      <input
                          id="password"
                          type="password"
                          value={password}
                          onChange={(event) => {
                              setPassword(event.target.value);
                              setError(undefined);
                          }}
                          className={inputClasses}
                          autoComplete="current-password"
                      />
                  </div>
              </div>

              {twoFactorRequired && (
                  <div className={twoFactorWrapperClasses}>
                      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600">
                          <FormattedMessage {...internationalization.twoFactorRequired}/>
                      </p>
                      <label className={fieldLabelClasses} htmlFor="twofactor">
                          <FormattedMessage {...internationalization.twoFactorCode}/>
                      </label>
                      <div className="relative flex items-center">
                          <input
                              id="twofactor"
                              type="text"
                              value={twoFactorCode}
                              onChange={(event) => setTwoFactorCode(event.target.value)}
                              className={clsx(inputClasses, 'text-center tracking-[0.32em] uppercase')}
                              autoComplete="one-time-code"
                          />
                      </div>
                      <button
                          id="submit-twofactor"
                          type="submit"
                          className={clsx(primaryButtonClasses, 'w-full sm:w-auto')}
                      >
                          <FormattedMessage {...internationalization.submitTwoFactor}/>
                      </button>
                  </div>
              )}

              {error && (
                  <div className={errorClasses}>
                      <FormattedMessage id={error.errorMessageId}/>
                  </div>
              )}

              <div className="flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                  <label className="inline-flex items-center gap-2 text-slate-600" htmlFor="stay-logged-in">
                      <input
                          id="stay-logged-in"
                          type="checkbox"
                          className={checkboxClasses}
                          checked={stayLoggedIn}
                          onChange={() =>
                              setStayLoggedIn((previous) => {
                                  setStayLoggedInPreference(!previous);
                                  return !previous;
                              })
                          }
                      />
                      <span>
                          <FormattedMessage {...internationalization.stayLoggedin}/>
                      </span>
                  </label>
                  <button
                      type="button"
                      onClick={passwordForgotten}
                      className={helperLinkClasses}
                  >
                      <FormattedMessage {...internationalization.passwordForgotten}/>
                  </button>
              </div>

              {!twoFactorRequired && (
                  <>
                      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
                          <button
                              id="login"
                              type="submit"
                              className={primaryButtonClasses}
                          >
                              <FormattedMessage {...internationalization.login}/>
                          </button>
                          <button
                              id="login-passkey"
                              type="button"
                              onClick={onPasskeyLogin}
                              className={outlineButtonClasses}
                          >
                              <FormattedMessage {...internationalization.loginWithPasskey}/>
                          </button>
                      </div>
                      <div className={dividerClasses}>
                          <span className={dividerBarClasses} />
                          <FormattedMessage {...internationalization.orLabel}/>
                          <span className={dividerBarClasses} />
                      </div>
                      <button
                          type="button"
                          onClick={redirectRegister}
                          className={secondaryButtonClasses}
                      >
                          <FormattedMessage {...internationalization.register}/>
                      </button>
                  </>
              )}
          </form>
          <TrademarkNotice className="mt-6 text-center text-xs text-slate-400"/>
      </MarketingAuthLayout>
  );
}

export default Login;
